import React, { useState, useEffect, useCallback } from 'react';
import { blogAPI } from '../utils/api';
import BlogCard from '../components/BlogCard';
import Button from '../components/Button';
import { ChevronDown, ChevronUp, Search, Filter } from 'lucide-react';

interface BlogPost {
  _id: string;
  title: string;
  excerpt: string;
  slug: string;
  categories: string[];
  createdAt: string;
  featuredImage?: string;
}

const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogAPI.getPosts(
        currentPage,
        6,
        selectedCategory || undefined,
        undefined,
        searchTerm || undefined,
        true
      );

      setPosts(response.data.blogPosts);
      setTotalPages(response.data.pages);

      // Extract unique categories if we don't have them yet
      if (categories.length === 0) {
        const allPosts = await blogAPI.getPosts(1, 100, undefined, undefined, undefined, true);
        const allCategories = allPosts.data.blogPosts.flatMap((post: BlogPost) => post.categories);
        const uniqueCategories = Array.from(new Set(allCategories)) as string[];
        setCategories(uniqueCategories);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to fetch blog posts', err);
      setError('Der opstod en fejl ved indlæsning af blogindlæg. Prøv igen senere.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory, searchTerm, categories.length]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPosts();
    setShowFilters(false);
  };

  const handleCategoryFilter = (category: string | null) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory(null);
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="bg-snudefryd-beige py-8 md:py-12">
        <div className="container-custom">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">Blog</h1>
          <p className="text-base md:text-lg text-gray-700 max-w-3xl">
            Læs vores ekspertartikler om hundeernæring, sundhedstips og råd til at holde din hund sund og glad.
          </p>
          
          {/* Filter toggle button - Mobile only */}
          <div className="mt-4 md:hidden">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-between w-full px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center">
                <Filter size={18} className="mr-2 text-gray-600" />
                <span className="font-medium">Filtre og søgning</span>
              </div>
              {showFilters ? 
                <ChevronUp size={18} className="text-gray-600" /> : 
                <ChevronDown size={18} className="text-gray-600" />
              }
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-6 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Sidebar - Hidden on mobile unless expanded */}
          <div className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden md:block'}`}>
            {/* Active filters - Mobile only */}
            {(selectedCategory || searchTerm) && (
              <div className="mb-4 md:hidden">
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedCategory && (
                    <span className="inline-block bg-snudefryd-light-purple text-snudefryd-blue px-3 py-1 rounded-full text-sm font-medium">
                      {selectedCategory}
                    </span>
                  )}
                  {searchTerm && (
                    <span className="inline-block bg-snudefryd-light-purple text-snudefryd-blue px-3 py-1 rounded-full text-sm font-medium">
                      "{searchTerm}"
                    </span>
                  )}
                </div>
                <button
                  onClick={resetFilters}
                  className="text-sm text-snudefryd-blue font-medium"
                >
                  Nulstil alle filtre
                </button>
              </div>
            )}
            
            {/* Search */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-4 md:mb-6">
              <h3 className="text-base md:text-lg font-bold mb-3">Søg</h3>
              <form onSubmit={handleSearch} className="flex">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Søg efter indlæg..."
                  className="flex-grow px-3 py-2 text-sm border border-gray-300 rounded-l-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                />
                <button
                  type="submit"
                  className="bg-snudefryd-blue text-white px-3 py-2 rounded-r-md hover:bg-opacity-90"
                >
                  <Search size={18} />
                </button>
              </form>
            </div>

            {/* Categories */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
              <h3 className="text-base md:text-lg font-bold mb-3">Kategorier</h3>
              <ul className="space-y-1">
                <li>
                  <button
                    onClick={() => handleCategoryFilter(null)}
                    className={`text-left w-full px-2 py-1 rounded-md text-sm ${
                      selectedCategory === null
                        ? 'bg-snudefryd-light-purple text-snudefryd-blue font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Alle kategorier
                  </button>
                </li>
                {categories.map((category, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handleCategoryFilter(category)}
                      className={`text-left w-full px-2 py-1 rounded-md text-sm ${
                        selectedCategory === category
                          ? 'bg-snudefryd-light-purple text-snudefryd-blue font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {category}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Blog Posts */}
          <div className="lg:col-span-3">
            {/* Filter info - Desktop only */}
            {(selectedCategory || searchTerm) && (
              <div className="hidden md:flex mb-6 justify-between items-center">
                <div className="flex flex-wrap gap-2">
                  {selectedCategory && (
                    <span className="inline-block bg-snudefryd-light-purple text-snudefryd-blue px-3 py-1 rounded-full text-sm font-medium">
                      Kategori: {selectedCategory}
                    </span>
                  )}
                  {searchTerm && (
                    <span className="inline-block bg-snudefryd-light-purple text-snudefryd-blue px-3 py-1 rounded-full text-sm font-medium">
                      Søgning: "{searchTerm}"
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                >
                  Nulstil filtre
                </Button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-snudefryd-blue mx-auto"></div>
                <p className="mt-4 text-gray-600">Indlæser blogindlæg...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <h3 className="text-xl font-bold text-red-700 mb-4">Ups!</h3>
                <p className="text-red-600 mb-6">{error}</p>
                <Button onClick={() => window.location.reload()} variant="primary">
                  Prøv igen
                </Button>
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <h3 className="text-xl font-bold text-yellow-700 mb-4">Ingen indlæg fundet</h3>
                <p className="text-yellow-600 mb-6">
                  Der blev ikke fundet nogen blogindlæg, der matcher dine søgekriterier.
                </p>
                <Button onClick={resetFilters} variant="primary">
                  Nulstil søgning
                </Button>
              </div>
            ) : (
              <>
                {/* Posts grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {posts.map((post) => (
                    <BlogCard
                      key={post._id}
                      id={post._id}
                      title={post.title}
                      slug={post.slug}
                      excerpt={post.excerpt}
                      featuredImage={post.featuredImage}
                      publishDate={post.createdAt}
                      categories={post.categories}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Forrige
                      </Button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "primary" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Button>
                      ))}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Næste
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage; 